import React, { ReactElement, useEffect, Suspense } from "react";

import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";

import Content from "@/components/content/content";
import Layout from "@/components/content/layout";

import {
  customPersister,
  queryClient,
} from "@/core/react-query/persistQueryClientSetup";
import GlobalLoader from "@/components/common/loading/GlobalLoader";
import { rootStore } from "@/mobx/store";
import "./assets/fonts/fonts.css";

dayjs.extend(utc); // dayjs.utc()
dayjs.extend(timezone); // dayjs.tz.setDefault('Asia/Seoul')
dayjs.extend(isBetween);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

dayjs.tz.setDefault("Asia/Seoul"); // 한국 시간대 설정
dayjs.locale("ko"); // 한국어 설정

// Error Boundary Component
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("App Error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            padding: "20px",
            textAlign: "center",
            fontFamily: "Arial, sans-serif",
          }}
        >
          <h2>앱에서 오류가 발생했습니다</h2>
          <p>페이지를 새로고침해주세요.</p>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: "10px 20px",
              backgroundColor: "#007bff",
              color: "white",
              border: "none",
              borderRadius: "5px",
              cursor: "pointer",
            }}
          >
            새로고침
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

function App(): ReactElement {
  console.log("App render", process.env.NODE_ENV);

  // 앱 초기화 시 토큰 복원
  useEffect(() => {
    try {
      // localStorage에서 토큰 복원
      const adminData = localStorage.getItem("admin");
      const userInfo = localStorage.getItem("user_info");
      const permissions = localStorage.getItem("permissions");

      if (adminData) {
        try {
          const parsed = JSON.parse(adminData);
          if (parsed.accessToken) {
            // 토큰을 store에 설정
            rootStore.setToken(parsed.accessToken);
            // me 객체도 복원 (signIn action 사용)
            rootStore.signIn(parsed, parsed.platform || "email");
          }
        } catch (error) {
          console.log("Admin data parse error:", error);
          // 파싱 실패 시 해당 데이터 삭제
          localStorage.removeItem("admin");
        }
      }

      if (userInfo) {
        try {
          const parsed = JSON.parse(userInfo);
          rootStore.setProfile(parsed);
        } catch (error) {
          console.log("User info parse error:", error);
          // 파싱 실패 시 해당 데이터 삭제
          localStorage.removeItem("user_info");
        }
      }

      // 권한 복원
      if (permissions) {
        try {
          const parsedPermissions = JSON.parse(permissions);
          rootStore.setPermissions(parsedPermissions);
        } catch (error) {
          console.log("Permissions parse error:", error);
          // 파싱 실패 시 해당 데이터 삭제
          localStorage.removeItem("permissions");
        }
      }
    } catch (error) {
      console.error("App initialization error:", error);
    }
  }, []);

  return (
    <ErrorBoundary>
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={{ persister: customPersister }}
      >
        <Layout>
          <Suspense fallback={<GlobalLoader />}>
            <Content />
          </Suspense>
          <GlobalLoader />
        </Layout>
        {process.env.NODE_ENV === "development" && (
          <ReactQueryDevtools buttonPosition="bottom-left" />
        )}
      </PersistQueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
